import {ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit} from "@angular/core";
import {OfferSearch} from "~/app/core/model/offer-search";
import {NetworkConnectionType} from "~/app/core/network.service";
import * as Log from "~/app/core/log";
import {InteractionType} from "~/app/core/model/interaction-type";
import {InteractionComponent} from "~/app/core/model/interaction-component";
import {InteractionRefType} from "~/app/core/model/interaction-ref-type";
import {ActivatedRoute, ParamMap} from "@angular/router";
import {BasePage} from "~/app/pages/base.page";
import {UserProperty} from "~/app/core/model/user-property";
import {OfferInfo} from "~/app/core/model/offer-info";
import {InitInfo} from "~/app/core/model/init-info";
import {formatTitleCase} from "~/app/core/util/string-util";
import {CoreService} from "~/app/core/core.service";

@Component({
    selector: "Searching",
    templateUrl: "./searching.page.html",
    styleUrls: ["./searching.page.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchingPage extends BasePage implements OnInit, OnDestroy {
    offerId: number = null;
    autoOpen: boolean = null;
    showAlert: boolean = false;
    searchStatus: string = "";

    private alertTimeout;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly activatedRoute: ActivatedRoute,
        private readonly cd: ChangeDetectorRef,
        private readonly core: CoreService,
    ) {
        super();
        // Use the component constructor to inject providers.
        this.updateStatus("Constructing");
        this.alertTimeout = setTimeout(() => {
            this.showAlert = true;
            this.cd.markForCheck();
        }, this.settings.searchAlertDelaySeconds * 1000);
    }

    ngOnInit(): void {
        // this.core.api.tickets_getTickets();
        this.offerId = Number(this.route.snapshot.paramMap.get("offerId"));
        // clear old cached files
        this.updateStatus("Cleaning old files");
        this.core.api.user_initDevice().then((info) => {
            this.updateStatus("Initialized user");
            Log.trace(Log.Tag.SearchingPage, "INIT: ");
            Log.trace(Log.Tag.SearchingPage, info);
            this.handleInit();
        }, (error) => {
            this.updateStatus("Error initializing user");
            Log.error(Log.Tag.SearchingPage, error);
            this.handleInit();
        }).catch((error) => {
            this.updateStatus("Error initializing user");
            Log.error(Log.Tag.SearchingPage, error);
            this.handleSearchError(error);
        });

        this.activatedRoute.queryParamMap.subscribe((value: ParamMap) => {
            this.autoOpen = false;
            Log.debug(Log.Tag.OffersPage, "params: " + JSON.stringify(value));
            if (value.has("autoOpen")) {
                this.autoOpen = value.get("autoOpen") === "true";
            }
        });
    }

    @HostListener("unloaded")
    ngOnDestroy(): void {
        // TODO why does this get called twice?
        Log.debug(Log.Tag.SearchingPage, "End searching.");
        this.updateStatus("Destroying");
        this.waiting = false;
        this.waitingSpinning = false;
        this.cd.markForCheck();
        clearTimeout(this.alertTimeout);
    }

    onAlertTap() {
        this.core.dialogs.confirm({
            title: "Reset?",
            message: "Sorry! Your app is experiencing problems. We can reset your local data.\n\nIf you continue to experience problems, please uninstall/reinstall the app.",
            okButtonText: "Reset",
            cancelButtonText: "Cancel"
        }).then((result) => {
            if (result === true) {
                this.core.api.resetData();
                this.navigateToSearchPage();
            }
        });
    }

    private handleInit() {
        try {
            this.updateStatus("Preparing search");
            const search: OfferSearch = this.core.api.offers_getSearch();
            if (Log.isTrace(Log.Tag.SearchingPage)) {
                Log.trace(Log.Tag.SearchingPage, "SEARCH: " + JSON.stringify(search));
            }
            if (!this.core.api.user_isWelcomed()) {
                this.navigateToWelcomePage();
            } else if (this.core.api.offers_searchExpired()) {
                if (search) {
                    this.updateStatus("Checking location");
                    this.core.api.location_isAllowLocation().then((allowed) => {
                        if (allowed) {
                            if (search.zipCode) {
                                // if the search expired and it was a zip code search and location is available, then do a location search instead
                                this.performGPSSearch(search, allowed);
                            } else {
                                if (this.core.api.offers_searchComplete()) {
                                    // if the search expired and it was a location search, then send to results screen do a location search again if network is not available
                                    Log.trace(Log.Tag.SearchingPage, "ALREADY HAVE OFFERS ALTHOUGH EXPIRED");
                                    // have offers, so redirect to offers page
                                    this.navigateToOffersPage(false);
                                } else {
                                    // if the search expired and it was a location search, then do a location search again if network is available
                                    this.performGPSSearch(search, allowed);
                                }
                            }
                        } else {
                            // if the search expired and it was a zip code search, but location isn't available, the direct the user to the search screen
                            this.performGPSSearch(search, allowed);
                        }
                    }, (error) => {
                        Log.trace(Log.Tag.SearchingPage, "allow location error");
                        this.handleSearchError(error);
                    }).catch((error) => {
                        Log.trace(Log.Tag.SearchingPage, "allow location catch error");
                        this.handleSearchError(error);
                    });
                } else {
                    this.navigateToSearchPage();
                }
            } else if (this.core.api.offers_searchComplete(this.offerId)) {
                Log.trace(Log.Tag.SearchingPage, "ALREADY HAVE OFFERS");
                // have offers, so redirect to offers page
                this.navigateToOffersPage(false);
            } else if (search) {
                if (this.core.net.connectionType === NetworkConnectionType.None) {
                    this.waiting = true;
                    this.waitingSpinning = false;
                    Log.trace(Log.Tag.SearchingPage, "no data available");
                    this.navigateToSearchPage();
                } else {
                    // have a search, so attempt it now
                    if (search.location) {
                        Log.trace(Log.Tag.SearchingPage, "search by location");
                        this.waiting = true;
                        this.core.api.location_isAllowLocation().then((allowed) => {
                            this.performGPSSearch(search, allowed);
                        }, (error) => {
                            Log.trace(Log.Tag.SearchingPage, "allow location error");
                            this.handleSearchError(error);
                        }).catch((error) => {
                            Log.trace(Log.Tag.SearchingPage, "allow catch location error");
                            this.handleSearchError(error);
                        });
                    } else if (search.zipCode && search.zipCode !== '0') {
                        this.performZipCodeSearch(search);
                    } else {
                        Log.trace(Log.Tag.SearchingPage, "go to search screen");
                        // no usable search condition, so go to search screen
                        this.navigateToSearchPage();
                    }
                }
            } else {
                Log.trace(Log.Tag.SearchingPage, "NEED TO SEARCH");
                // have neither search nor offers, so redirect to location search page
                this.navigateToSearchPage();
            }
        } catch (e) {
            Log.trace(Log.Tag.SearchingPage, e);
            this.handleSearchError(new Error());
        }
    }

    private performGPSSearch(search: OfferSearch, allowed: boolean) {
        this.updateStatus("Performing search");
        this.waiting = true;
        this.cd.markForCheck();
        search.location = true;
        this.core.api.offers_setSearch(search);

        if (allowed) {
            // perform location search
            this.updateStatus("Finding location");
            this.core.api.location_getLocationInfo().then((info) => {
                Log.trace(Log.Tag.SearchingPage, info);
                this.core.api.interaction(InteractionType.SearchGPS, InteractionComponent.SearchingPage, InteractionRefType.GPS);
                this.updateStatus("Performing GPS search");
                this.core.api.offers_performSearch().then((result) => {
                    Log.trace(Log.Tag.SearchingPage, "search complete");
                    if (info) {
                        this.core.api.user_captureLocation({ longitude: info.longitude, latitude: info.latitude });
                    }
                    this.navigateToOffersPage(true);
                }, (error) => {
                    Log.trace(Log.Tag.SearchingPage, "search error");
                    this.handleSearchError(error);
                }).catch((error) => {
                    Log.trace(Log.Tag.SearchingPage, "search catch error");
                    this.handleSearchError(error);
                });
            }, (error) => {
                Log.trace(Log.Tag.SearchingPage, "error getting location");
                this.handleSearchError(error);
            }).catch((error) => {
                Log.trace(Log.Tag.SearchingPage, "catch location error");
                this.handleSearchError(error);
            });
        } else {
            Log.trace(Log.Tag.SearchingPage, "location not allowed");
            // location search no allowed, so redirect to search screen
            this.navigateToSearchPage();
        }
    }

    private performZipCodeSearch(search: OfferSearch) {
        Log.trace(Log.Tag.SearchingPage, "search by zip");
        // perform zip code search
        this.waiting = true;
        this.cd.markForCheck();
        this.core.api.interaction(InteractionType.SearchZip, InteractionComponent.SearchingPage, InteractionRefType.ZipCode, Number(search.zipCode));
        this.updateStatus("Performing zip code search");
        this.core.api.offers_performSearch().then((result) => {
            Log.trace(Log.Tag.SearchingPage, "zip search complete");
            this.core.api.user_captureLocation({ zip: String(search.zipCode) });
            this.navigateToOffersPage(false);
        }, (error) => {
            Log.trace(Log.Tag.SearchingPage, "zip search error");
            this.handleSearchError(error);
        }).catch((error) => {
            Log.trace(Log.Tag.SearchingPage, "zip search catch error");
            this.handleSearchError(error);
        });
    }

    private navigateToWelcomePage() {
        this.waiting = false;
        this.updateStatus("Exiting to welcome");
        this.core.nav.navPageWelcome();
    }

    private navigateToOffersPage(gpsSearch: boolean) {
        this.updateStatus("Exiting to offers");
        if (this.core.api.offers_haveOffers()) {
            this.waiting = false;
            if (gpsSearch) {
                this.core.api.user_setProperty(UserProperty.OutOfArea, "false");
            }
            if (this.core.api.device_loadSettings().signupBeforeAnyOffers && !this.core.api.user_isAuthenticated()) {
                const info: InitInfo = this.core.api.device_info();
                if (info && info.showLogin) {
                    this.core.nav.navPageLogin();
                } else {
                    this.core.nav.navPageRegister();
                }
            } else {
                const offers: Array<OfferInfo> = this.core.api.offers_loadOffers(false);
                if (offers && offers.length > 0) {
                    const offer = offers[0];
                    if (offer.addresses && offer.addresses.length > 1 && offer.addresses[0].state && offer.addresses[0].city) {
                        this.core.api.user_setProperty(UserProperty.Area, formatTitleCase(offer.addresses[0].city + ", " + offer.addresses[0].state));
                    }
                }
                this.core.nav.navPageOffers(this.offerId, this.autoOpen);
                // setTimeout(() => {
                //     this.core.nav.navPageOffers(this.offerId, this.autoOpen);
                // }, 1000);
            }
        } else {
            this.waiting = false;
            if (gpsSearch) {
                this.core.api.user_setProperty(UserProperty.OutOfArea, "true");
            }
            if (this.core.api.device_loadSettings().signupBeforeNoOffers && !this.core.api.user_isAuthenticated()) {
                const info: InitInfo = this.core.api.device_info();
                if (info && info.showLogin) {
                    this.core.nav.navPageLogin();
                } else {
                    this.core.nav.navPageRegister();
                }
            } else {
                this.core.nav.navPageNoOffers();
            }
        }
    }

    private navigateToSearchPage() {
        this.updateStatus("Exiting to search location");
        this.waiting = false;
        this.core.nav.navPageSearch(false);
    }

    private handleSearchError(error) {
        Log.trace(Log.Tag.SearchingPage, "search error");
        Log.trace(Log.Tag.SearchingPage, error);
        this.waiting = false;
        if (String(error).indexOf("Timeout") !== -1) {
            this.core.toast.toastError({
                header: "Problem Searching",
                message: "Sorry! We're having trouble finding your GPS location right now.\n\nTry again.",
                onHide: () => {
                    Log.trace(Log.Tag.SearchingPage, "timeout waiting for GPS");
                    this.navigateToSearchPage();
                }
            });
        } else {
            this.showAlert = true;
            this.cd.markForCheck();
            this.core.toast.toastError({
                header: "Problem Searching",
                message: "An unknown problem was encountered while searching for offers.",
                duration: 10000,
                onHide: () => {
                    Log.trace(Log.Tag.SearchingPage, "unknown problem searching");
                    this.navigateToSearchPage();
                }
            });
        }
    }

    private updateStatus(status: string) {
        this.searchStatus = status;
        this.cd.markForCheck();
    }
}
